import {Typography} from 'components/Typography';
import {useICheckHasBarInText} from 'modules/wineshops-list/hooks/useICheckHasBarInText';
import {FC, useMemo} from 'react';
import {WineShopPhone} from 'modules/wineshops-list/components/WineShopPhone';
import {WineShopPaymentMethods} from 'modules/wineshops-list/components/WineShopPaymentMethods';
import InfoWysiwyg from 'modules/info/components/InfoWysiwyg';
import {MetroStationWalkingTime} from 'modules/wineshops-list/components/MetroStationWalkingTime';
import style from './style.module.scss';
import {Col, Row} from 'components/Grid';
import {Slider} from 'modules/slider/components/Slider';
import {AddressFilterItem} from 'modules/wineshops-list/components/AddressFilterItem';
import {IMetroStation, IWineShop} from 'modules/wineshops-list/models';
import {getWalkTimeValue} from 'modules/wineshops-list/helpers';
import {getMediaSrc} from 'core/helpers/media';
import {useShopSchedule} from 'modules/wineshops-list/hooks/useShopSchedule';
import {WorkShopSchedule} from 'modules/wineshops-list/components/WorkShopSchedule';
import {BySimpleInfo} from '../BySimpleInfo';
import {adaptive} from 'styles/mixins/Adaptive';
import {WINE_SHOPS_STOCK_TYPES_CODES} from 'modules/wineshops-list/constants';
import {useBySimple} from 'modules/wineshops-list/hooks/useBySimple';

interface IProps {
    data: IWineShop;
}

const SLIDER_BREAKPOINTS = {
    [adaptive.SIZE_TABLET_SM_MIN]: {
        slidesPerView: 1,
    },
    [adaptive.SIZE_SM_MIN]: {
        slidesPerView: 1.05,
        spaceBetween: 8,
    },
} as const;

export const WineShopMainInfo: FC<IProps> = ({data}) => {
    const schedule = useShopSchedule(data.typedSchedule);
    const restSchedule = useShopSchedule(data.typedRestSchedule);

    const isNotification = data.filters?.some(({code}) => 'pick_up' === code || 'to_book' === code);

    const isBar = useICheckHasBarInText(0 < restSchedule?.length, data.title);

    const isBySimple = useBySimple(data);

    const metroStationsWithWalkingTime: {
        metroStation: IMetroStation;
        walkingTime: string;
    }[] = useMemo(() => {
        if (!data.geoposition) {
            return [];
        }

        return (data.metroStations ?? []).map((metroStation) => ({
            metroStation,
            walkingTime: getWalkTimeValue(metroStation, data.geoposition),
        }));
    }, [data.geoposition, data.metroStations]);

    return (
        <>
            <Row>
                <Col sm={12}>
                    <div className={style.tags}>
                        {data.filters?.map(({value, code}) => (
                            <AddressFilterItem key={code}>{value}</AddressFilterItem>
                        ))}
                        {isBySimple && (
                            <AddressFilterItem key={WINE_SHOPS_STOCK_TYPES_CODES.BY_SIMPLE}>
                                by Simple
                            </AddressFilterItem>
                        )}
                    </div>
                </Col>
            </Row>
            <Row className={style.row}>
                <Col md={6} sm={12}>
                    {0 < data.photos?.length ? (
                        <div>
                            <Slider
                                breakpoints={SLIDER_BREAKPOINTS}
                                className={style.slider}
                                isNavigation={true}
                                isPagination={true}
                                isWhiteNavButtons={true}
                                spaceBetween={8}
                            >
                                {data.photos?.map((image: string) => {
                                    return (
                                        <div className={style.image} key={image}>
                                            <picture>
                                                <img
                                                    alt={data.title}
                                                    itemProp={'image'}
                                                    loading={'eager'}
                                                    src={getMediaSrc(image, {
                                                        isWebp: true,
                                                        width: 1500,
                                                    })}
                                                />
                                            </picture>
                                        </div>
                                    );
                                })}
                            </Slider>
                        </div>
                    ) : (
                        <img
                            alt={data.title}
                            className={style.img}
                            height={660}
                            itemProp={'image'}
                            src={getMediaSrc(data.mainPhoto, {isWebp: true, width: 1500})}
                            width={660}
                        />
                    )}
                </Col>
                <Col md={6} sm={12}>
                    <div className={style.info}>
                        <div className={style.infoCol}>
                            <Typography className={style.address} element={'div'} itemProp={'address'}>
                                {data.address}
                            </Typography>
                            <div className={style.metrostations}>
                                {metroStationsWithWalkingTime.map(({metroStation, walkingTime}, index) => (
                                    <MetroStationWalkingTime
                                        {...metroStation}
                                        key={`${metroStation?.name}-${index}`}
                                        nameTextColor={'gray100'}
                                        nameTextVariant={'p'}
                                        walkingTime={walkingTime}
                                    />
                                ))}
                            </div>
                            <div className={style.schedule}>
                                <Typography color={'gray60'} element={'div'}>
                                    Винотека
                                </Typography>
                                <WorkShopSchedule schedule={schedule} textColor={'gray60'} />
                                {0 < restSchedule?.length && (
                                    <>
                                        <Typography color={'gray60'} element={'div'}>
                                            {isBar ? 'Бар' : 'Ресторан'}
                                        </Typography>
                                        <WorkShopSchedule schedule={restSchedule} textColor={'gray60'} />
                                    </>
                                )}
                            </div>
                            <div className={style.telContainer}>
                                <Typography
                                    className={style.tel}
                                    color={'gray60'}
                                    element={'div'}
                                    variant={'button-14-m-cl'}
                                >
                                    Телефон
                                </Typography>
                                {data?.phone && <WineShopPhone phone={data?.phone} />}
                            </div>
                        </div>
                        <div className={style.infoCol}>
                            <Typography
                                className={style.paymentsTitle}
                                color={'gray60'}
                                element={'div'}
                                variant={'button-14-m-cl'}
                            >
                                Принимаем к оплате
                            </Typography>
                            <WineShopPaymentMethods payments={data?.availablePaymentMethods} />
                            {isNotification && (
                                <Typography className={style.notification} element={'div'} variant={'label-14-r'}>
                                    В этой винотеке можно забрать заказ или оформить бронирование
                                </Typography>
                            )}
                            {isBySimple && (
                                <BySimpleInfo
                                    dataAutotestTarget={'winery-map-item'}
                                    dataAutotestTargetId={`winery-list-map-bysimple-${data?.id}`}
                                />
                            )}
                        </div>
                    </div>
                    <div className={style.getTo}>
                        <div className={style.goToTitle}>Как проехать</div>
                        <InfoWysiwyg content={data?.description} itemProp={'description'} />
                    </div>
                </Col>
            </Row>
        </>
    );
};
